/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import useIntl from 'react-intl/src/components/useIntl'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useContext } from 'react'
import { noop } from 'lodash'

import type { OrganizationOption } from '@modules/organization-api'
import { getErrorCode } from '@modules/utils/error-react-query'
import { addToast } from '@modules/cui/Toasts'
import { logoutUrl } from '@modules/auth/urls'
import AuthContext from '@modules/auth/context'

import { isOrganizationBoundAuthMethod } from '../../auth/authMethodUtils'

import { messages } from './messages'

import { useSwitchOrganizationMutation } from '.'
export const useSwitchOrganization = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo') ?? undefined
  const { mutate: switchOrganizationMutate, isLoading } = useSwitchOrganizationMutation()
  const { setAuthLastLogin, authContext } = useContext(AuthContext)

  const onSwitchOrganization = ({
    organizationId,
    onSuccessSwitchingOrganization,
  }: {
    organizationId: string
    onSuccessSwitchingOrganization: () => void
  }) => {
    switchOrganizationMutate(
      { organizationId, redirectTo },
      {
        onSuccess: () => {
          // switch organization can only target non-enforced login organizations, that is why the method cannot be byoidp
          setAuthLastLogin({
            method: isOrganizationBoundAuthMethod(authContext.method)
              ? 'username-and-password'
              : authContext.method,
            organizationId,
            ssoLoginIdentifier: undefined,
          })
          onSuccessSwitchingOrganization()
        },
        onError: (error) => {
          const errorCode = getErrorCode(error)

          switch (errorCode) {
            case 'auth.organization_membership_required':
              addToast({
                color: 'danger',
                title: formatMessage(
                  messages.organizationSwitchConfirmationErrorMembershipRequired,
                ),
              })
              return
            case 'organization.not_found':
              addToast({
                color: 'danger',
                title: formatMessage(
                  messages.organizationSwitchConfirmationErrorOrganizationNotFound,
                ),
              })
              return
            default:
              addToast({
                color: 'danger',
                title: formatMessage(messages.organizationSwitchConfirmationError),
              })
              return
          }
        },
      },
    )
  }

  const switchOrganization = ({
    organization,
    onSuccessSwitchingOrganization = noop,
  }: {
    organization: Pick<
      OrganizationOption,
      'organization_id' | 'enforce_authentication_method' | 'sso_login_identifier'
    >
    onSuccessSwitchingOrganization?: () => void
  }) => {
    switch (organization.enforce_authentication_method) {
      case 'sso':
        const ssoLoginIdentifier = organization.sso_login_identifier
        navigate({
          pathname: logoutUrl(),
          search: new URLSearchParams({
            reason: 'switch_to_sso_enforcement_organization',
            ssoLoginIdentifier: ssoLoginIdentifier ?? '',
            redirectTo: redirectTo ?? window.location.pathname,
          }).toString(),
        })
        break
      case 'social':
        navigate({
          pathname: logoutUrl(),
          search: new URLSearchParams({
            reason: 'switch_to_social_enforcement_organization',
            redirectTo: redirectTo ?? window.location.pathname,
          }).toString(),
        })
        break
      default:
        onSwitchOrganization({
          organizationId: organization.organization_id,
          onSuccessSwitchingOrganization,
        })
        break
    }
  }

  const onConfirmSsoOption = ({
    organizationSsoLoginIdentifier,
  }: {
    organizationSsoLoginIdentifier: NonNullable<OrganizationOption['sso_login_identifier']>
  }) => {
    navigate({
      pathname: logoutUrl(),
      search: new URLSearchParams({
        reason: 'switch_to_sso_enforcement_organization',
        ssoLoginIdentifier: organizationSsoLoginIdentifier,
        redirectTo: redirectTo ?? window.location.pathname,
      }).toString(),
    })
  }

  return {
    onConfirmSsoOption,
    switchOrganization,
    isLoading,
  }
}
